/**
 * Geodesic area of an Area-of-Operation bounding box.
 *
 * Uses the exact area of a latitude/longitude rectangle on a sphere:
 *   A = R² · Δλ · (sin φ₂ − sin φ₁)
 * where Δλ is the longitude span in radians and φ₁/φ₂ are the south/north
 * latitudes. This is accurate enough for the small boxes used for map bundles.
 */

import { AoOBounds } from '../types/mapBundle';

const EARTH_RADIUS_KM = 6371;

/** Maximum Area of Operation size (km²) allowed for a map bundle. */
export const MAX_AOO_AREA_KM2 = 10;

const toRadians = (deg: number): number => (deg * Math.PI) / 180;

/** Area of the bounding box in square kilometres. */
export function boundingBoxAreaKm2(bounds: AoOBounds): number {
    const { aoo_north, aoo_south, aoo_east, aoo_west } = bounds;
    const lonSpan = toRadians(aoo_east - aoo_west);
    const latTerm = Math.sin(toRadians(aoo_north)) - Math.sin(toRadians(aoo_south));
    return Math.abs(EARTH_RADIUS_KM * EARTH_RADIUS_KM * lonSpan * latTerm);
}

/** Human-readable area, switching to m² for boxes smaller than 1 km². */
export function formatArea(km2: number): string {
    if (!Number.isFinite(km2) || km2 < 0) return '—';
    if (km2 >= 1) {
        const digits = km2 >= 100 ? 0 : km2 >= 10 ? 1 : 2;
        return `${km2.toLocaleString('en-US', { maximumFractionDigits: digits })} km²`;
    }
    const m2 = km2 * 1_000_000;
    return `${Math.round(m2).toLocaleString('en-US')} m²`;
}
